import { useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Minus, Plus, Search } from "lucide-react";

import type { Account } from "../../services/financialService";

interface Props {
  /** The whole chart -- the picker draws the tree from it, so parents of a
   *  selectable subaccount stay visible even when they cannot be posted to. */
  accounts?: Account[];
  /** Which accounts may actually be chosen. Everything else is shown as a
   *  heading you can expand but not pick. Defaults to "anything approved". */
  isSelectable?: (account: Account) => boolean;
  /** Controlled value (account id as a string, "" for nothing chosen). */
  value?: string;
  /** Uncontrolled starting value -- mirrors <select defaultValue>. */
  defaultValue?: string;
  onChange?: (value: string) => void;
  /** Submits with the surrounding form, exactly like the <select> it replaces. */
  name?: string;
  required?: boolean;
  disabled?: boolean;
  /** Shown while nothing is chosen; doubles as the label of the "clear" row. */
  placeholder?: string;
  className?: string;
  ariaLabel?: string;
}

interface Node {
  account: Account;
  children: Node[];
  depth: number;
  selectable: boolean;
}

const byCode = (a: Account, b: Account) =>
  a.code.localeCompare(b.code, undefined, { numeric: true, sensitivity: "base" });

const approved = (account: Account) => account.workflow_status === "approved";

/** Roll the flat account list into main accounts with their subaccounts under
 *  them, dropping branches that hold nothing this picker can select. */
const buildTree = (accounts: Account[], isSelectable: (account: Account) => boolean): Node[] => {
  const known = new Set(accounts.map((account) => account.id));
  const childrenOf = new Map<number | "root", Account[]>();
  for (const account of accounts) {
    const key = account.parent && known.has(account.parent) && account.parent !== account.id ? account.parent : "root";
    const siblings = childrenOf.get(key);
    if (siblings) siblings.push(account);
    else childrenOf.set(key, [account]);
  }

  const build = (key: number | "root", depth: number, seen: Set<number>): Node[] =>
    (childrenOf.get(key) ?? [])
      .filter((account) => !seen.has(account.id))
      .sort(byCode)
      .map((account) => ({
        account,
        depth,
        selectable: isSelectable(account),
        children: build(account.id, depth + 1, new Set([...seen, account.id])),
      }))
      .filter((node) => node.selectable || node.children.length > 0);

  return build("root", 0, new Set());
};

/** Keep the accounts whose code or name matches, plus the parents they hang off
 *  so a match is never shown adrift from its main account. */
const filterTree = (nodes: Node[], query: string): Node[] => {
  const needle = query.trim().toLowerCase();
  if (!needle) return nodes;
  const keep = (node: Node): Node | null => {
    const children = node.children.map(keep).filter((child): child is Node => child !== null);
    const hit = `${node.account.code} ${node.account.name}`.toLowerCase().includes(needle);
    if (!hit && !children.length) return null;
    return { ...node, children: hit ? node.children : children };
  };
  return nodes.map(keep).filter((node): node is Node => node !== null);
};

export default function AccountSelect({
  accounts,
  isSelectable = approved,
  value,
  defaultValue = "",
  onChange,
  name,
  required,
  disabled,
  placeholder = "Select an account…",
  className = "",
  ariaLabel,
}: Props) {
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue);
  const selectedId = isControlled ? value : internalValue;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [activeIndex, setActiveIndex] = useState(0);
  // The panel is portalled to the body so cards with `overflow-hidden` cannot
  // clip it, which means its position has to be measured rather than inherited.
  const [panelBox, setPanelBox] = useState<{ left: number; width: number; top?: number; bottom?: number }>();

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const list = useMemo(() => accounts ?? [], [accounts]);
  const tree = useMemo(() => buildTree(list, isSelectable), [list, isSelectable]);
  const visibleTree = useMemo(() => filterTree(tree, query), [tree, query]);
  const selected = list.find((account) => String(account.id) === selectedId);

  // While searching every branch is open, so a match never hides behind a "+".
  const isExpanded = (node: Node) => (query.trim() ? true : expanded.has(node.account.id));

  const rows = useMemo(() => {
    const flat: Node[] = [];
    const walk = (nodes: Node[]) => {
      for (const node of nodes) {
        flat.push(node);
        if (node.children.length && isExpanded(node)) walk(node.children);
      }
    };
    walk(visibleTree);
    return flat;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleTree, expanded, query]);

  const selectableAccounts = useMemo(() => list.filter(isSelectable), [list, isSelectable]);

  const close = () => { setOpen(false); setQuery(""); };

  const place = () => {
    const box = triggerRef.current?.getBoundingClientRect();
    if (!box) return;
    const dropUp = box.bottom + 340 > window.innerHeight && box.top > window.innerHeight - box.bottom;
    setPanelBox({
      left: box.left,
      width: box.width,
      ...(dropUp ? { bottom: window.innerHeight - box.top + 4 } : { top: box.bottom + 4 }),
    });
  };

  const commit = (next: string) => {
    if (!isControlled) setInternalValue(next);
    onChange?.(next);
    close();
    triggerRef.current?.focus();
  };

  const toggle = (id: number) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Opening on a chosen subaccount reveals it in place rather than making the
  // user hunt for which main account it sits under.
  useEffect(() => {
    if (!open) return;
    const byId = new Map(list.map((account) => [account.id, account]));
    const chain = new Set<number>();
    let parentId = byId.get(Number(selectedId))?.parent;
    while (parentId && !chain.has(parentId)) {
      chain.add(parentId);
      parentId = byId.get(parentId)?.parent;
    }
    if (chain.size) setExpanded((current) => new Set([...current, ...chain]));
    setActiveIndex(0);
    place();
    const reposition = () => place();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    const focus = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
      window.clearTimeout(focus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!containerRef.current?.contains(target) && !panelRef.current?.contains(target)) close();
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  useEffect(() => {
    listRef.current?.querySelector(`[data-row="${activeIndex}"]`)?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, rows.length]);

  const onKeyDown = (event: ReactKeyboardEvent) => {
    if (event.key === "Escape") { event.preventDefault(); close(); triggerRef.current?.focus(); return; }
    if (event.key === "ArrowDown") { event.preventDefault(); setActiveIndex((i) => Math.min(i + 1, rows.length - 1)); return; }
    if (event.key === "ArrowUp") { event.preventDefault(); setActiveIndex((i) => Math.max(i - 1, 0)); return; }
    const active = rows[activeIndex];
    if (!active) return;
    if (event.key === "ArrowRight" && active.children.length && !isExpanded(active)) {
      event.preventDefault();
      toggle(active.account.id);
    } else if (event.key === "ArrowLeft" && active.children.length && isExpanded(active)) {
      event.preventDefault();
      toggle(active.account.id);
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (active.selectable) commit(String(active.account.id));
      else if (active.children.length) toggle(active.account.id);
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        className="input items-center justify-between gap-2 text-left"
        aria-label={ariaLabel}
        aria-haspopup="tree"
        aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
        onKeyDown={(event) => {
          if (!open && (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        <span className={`truncate ${selected ? "text-gray-900" : "text-secondary-500"}`}>
          {selected ? <><span className="font-mono">{selected.code}</span> · {selected.name}</> : placeholder}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
      </button>

      {/* Carries the value into the surrounding form's FormData and lets the
          browser enforce `required`, the way the plain <select> used to. */}
      {(name || required) && (
        <select
          name={name}
          required={required}
          disabled={disabled}
          value={selectedId}
          onChange={() => undefined}
          tabIndex={-1}
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 h-full w-full opacity-0"
        >
          <option value="" />
          {selectableAccounts.map((account) => (
            <option key={account.id} value={account.id}>{account.code} · {account.name}</option>
          ))}
        </select>
      )}

      {open && panelBox && createPortal(
        <div
          ref={panelRef}
          style={{ position: "fixed", left: panelBox.left, top: panelBox.top, bottom: panelBox.bottom, width: Math.max(panelBox.width, 288) }}
          className="z-50 rounded-md border border-secondary-200 bg-white shadow-lg"
          onKeyDown={onKeyDown}
        >
          <div className="flex items-center gap-2 border-b border-secondary-100 px-3 py-2">
            <Search className="h-4 w-4 shrink-0 text-gray-400" />
            <input
              ref={searchRef}
              value={query}
              onChange={(event) => { setQuery(event.target.value); setActiveIndex(0); }}
              placeholder="Search code or name…"
              className="w-full border-0 p-0 text-sm outline-none placeholder:text-secondary-400"
            />
          </div>
          <div ref={listRef} role="tree" className="max-h-72 overflow-y-auto py-1 text-sm">
            {!required && (
              <button
                type="button"
                className={`flex w-full items-center px-3 py-1.5 text-left hover:bg-secondary-50 ${selectedId ? "text-gray-600" : "font-medium text-primary-700"}`}
                onClick={() => commit("")}
              >
                {placeholder}
              </button>
            )}
            {rows.map((node, index) => {
              const { account } = node;
              const chosen = String(account.id) === selectedId;
              const expandable = node.children.length > 0;
              const showing = isExpanded(node);
              return (
                <div
                  key={account.id}
                  data-row={index}
                  role="treeitem"
                  aria-selected={chosen}
                  aria-expanded={expandable ? showing : undefined}
                  className={`flex items-center ${index === activeIndex ? "bg-secondary-50" : ""}`}
                  onMouseEnter={() => setActiveIndex(index)}
                >
                  <span className="flex shrink-0" style={{ paddingLeft: 8 + node.depth * 16 }}>
                    {expandable ? (
                      <button
                        type="button"
                        tabIndex={-1}
                        aria-label={showing ? `Hide subaccounts of ${account.name}` : `Show subaccounts of ${account.name}`}
                        className="mr-1 rounded border border-secondary-200 p-0.5 text-gray-500 hover:bg-secondary-100"
                        onClick={(event) => { event.stopPropagation(); toggle(account.id); }}
                      >
                        {showing ? <Minus className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
                      </button>
                    ) : (
                      <span className="mr-1 w-[1.125rem]" />
                    )}
                  </span>
                  <button
                    type="button"
                    className={`flex-1 truncate py-1.5 pr-3 text-left ${
                      node.selectable ? "hover:text-primary-700" : "cursor-default text-gray-500"
                    } ${chosen ? "font-semibold text-primary-700" : ""}`}
                    onClick={() => (node.selectable ? commit(String(account.id)) : expandable && toggle(account.id))}
                  >
                    <span className="font-mono">{account.code}</span> · {account.name}
                    {!node.selectable && <span className="ml-2 text-xs text-gray-400">(heading)</span>}
                    {expandable && !showing && (
                      <span className="ml-2 text-xs text-gray-400">
                        {node.children.length} subaccount{node.children.length === 1 ? "" : "s"}
                      </span>
                    )}
                    {chosen && <Check className="ml-2 inline h-3.5 w-3.5" />}
                  </button>
                </div>
              );
            })}
            {!rows.length && (
              <p className="px-3 py-3 text-gray-500">
                {list.length ? "No account matches that search." : "No accounts available yet."}
              </p>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
